/** Cold-load document deep link waiting for workspace list (module scope survives StrictMode remount). */
export type PendingDocumentDeepLink = {
  pathname: string
  pathOrId: string
}

let sessionPendingDocumentDeepLink: PendingDocumentDeepLink | null = null
let coldLoadCaptureAttempted = false

export function getPendingDocumentDeepLink(): PendingDocumentDeepLink | null {
  return sessionPendingDocumentDeepLink
}

export function setPendingDocumentDeepLink(
  entry: PendingDocumentDeepLink | null,
): void {
  sessionPendingDocumentDeepLink = entry
}

/** Clear cold deep-link pending (module scope). Pair with url-sync ref sync. */
export function clearDocumentDeepLinkPending(): void {
  sessionPendingDocumentDeepLink = null
}

export function isDocumentDeepLinkRestorePending(): boolean {
  return sessionPendingDocumentDeepLink != null
}

function asAppPath(value: string): string {
  return value.startsWith('/') ? value : `/${value}`
}

/** True when this open is the document the cold deep link is still restoring. */
export function pendingDeepLinkMatchesContentId(
  contentId: string,
  pending: PendingDocumentDeepLink | null,
  pathsEquivalent: (left: string, right: string) => boolean = (left, right) => left === right,
): boolean {
  if (!pending || !contentId) return false
  if (contentId === pending.pathOrId) return true
  const idPath = asAppPath(contentId)
  if (pathsEquivalent(idPath, pending.pathname)) return true
  if (pathsEquivalent(idPath, asAppPath(pending.pathOrId))) return true
  return false
}

/**
 * A failed open during a cold restore.
 * `ignore` leaves a different in-progress deep link alone.
 */
export function documentOpenMissDisposition(args: {
  extensionless: boolean
  pathHasSlash: boolean
  failureIsPending: boolean
  restorePending: boolean
}): 'explorer' | 'ignore' | 'keep-provisional' | 'home' {
  if (!args.failureIsPending && args.restorePending) return 'ignore'
  if (args.pathHasSlash && !args.extensionless) return 'explorer'
  if (args.failureIsPending && args.restorePending) return 'keep-provisional'
  return 'home'
}

/** Retry the pending path only when the active note tab is that same document. */
export function shouldRetryPendingDocumentRestore(args: {
  tabMatchesPending: boolean
  workspacesReady: boolean
  activeTabType?: string
  pendingTabResolved: boolean
}): boolean {
  return (
    args.workspacesReady &&
    args.tabMatchesPending &&
    args.activeTabType === 'note' &&
    !args.pendingTabResolved
  )
}

export function markColdLoadCaptureAttempted(): void {
  coldLoadCaptureAttempted = true
}

export function wasColdLoadCaptureAttempted(): boolean {
  return coldLoadCaptureAttempted
}

/** Test-only reset (vitest). */
export function __resetDocumentDeepLinkPendingForTests(): void {
  sessionPendingDocumentDeepLink = null
  coldLoadCaptureAttempted = false
}
